import React, { FC } from 'react';
import {
  Button,
  Text,
  ListGroup,
  Modal,
  Overlay,
} from '@cubeyou-team/hx-react-components';
import classNames from 'classnames';

import translations from 'src/lang';

import { usePostTrialUpgradeRequest } from 'src/modules/trial-paywall/api/hooks/use-post-trial-upgrade-request';
import type { TrialFeature } from '@discover-frontend/components';
import { UNLOCK_FULL_VERSION_IMAGE_URL } from '../../constants/images';
import './index.css';

const { TRIAL_PAYWALL } = translations['en-US'];

const KEYS = {
  CONTACT: 'trial-paywall-contact',
  CLOSE: 'trial-paywall-close',
};

const MODAL_WIDTH = '33rem';

interface UnlockFullVersionModalProps {
  isOpen: boolean;
  feature: TrialFeature | null;
  onClose: () => void;
}

export const UnlockFullVersionModal: FC<UnlockFullVersionModalProps> = ({
  isOpen,
  feature,
  onClose,
}) => {
  const { mutate, isLoading } = usePostTrialUpgradeRequest(onClose);

  const handleContact = () => {
    if (!feature) return;

    mutate({ feature });
  };

  return (
    <Overlay isOpen={isOpen} width={MODAL_WIDTH}>
      <Modal
        title={TRIAL_PAYWALL.MODAL.TITLE}
        closable
        handleClose={onClose}
        footer={
          <ListGroup
            direction="row"
            items={[
              <Button
                key={KEYS.CONTACT}
                type="button"
                size="small"
                onClick={handleContact}
                disabled={!feature || isLoading}
                loading={isLoading || undefined}
                data-test-id={KEYS.CONTACT}
              >
                {TRIAL_PAYWALL.MODAL.BUTTONS.CONTACT}
              </Button>,
              <Button
                key={KEYS.CLOSE}
                type="button"
                size="small"
                variant="outlined"
                onClick={onClose}
                data-test-id={KEYS.CLOSE}
              >
                {TRIAL_PAYWALL.MODAL.BUTTONS.CLOSE}
              </Button>,
            ]}
          />
        }
      >
        <div
          className="relative shrink-0 h-60"
          role="img"
          aria-label={TRIAL_PAYWALL.MODAL.IMAGE_ALT}
        >
          <div className="absolute inset-0 z-0">
            <div className="absolute top-2 -right-8 h-rem-9.5 w-40 bg-hx-primary-100">
              <div
                aria-hidden
                className={classNames(
                  'triangle-border',
                  'absolute left-0 top-1/2 h-0 w-0 transform -translate-y-1/2'
                )}
              />
            </div>
          </div>
          <div className="absolute -bottom-8 -left-8 h-40 w-40 bg-hx-primary-100 rounded-full z-0" />
          <img
            aria-hidden
            alt=""
            src={UNLOCK_FULL_VERSION_IMAGE_URL}
            className="absolute -bottom-8 -right-8 h-280 w-340 transform -scale-x-100 object-contain"
          />
          <Text
            weight="bold"
            size="heading"
            tag="h3"
            className="absolute top-8 w-60 text-4xl leading-custom-1.2"
          >
            {TRIAL_PAYWALL.MODAL.MESSAGE}
          </Text>
        </div>
      </Modal>
    </Overlay>
  );
};
